// src/pages/admin/opex/OpexInvoiceDetails.jsx
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar
} from '@mui/material';
import { ArrowBack, CheckCircle, Cancel, Download } from '@mui/icons-material';
import MainCard from 'components/MainCard';
import InvoiceStatusChip from 'components/invoices/InvoiceStatusChip';
import opexInvoiceService from 'services/opexInvoiceService';
import { formatCurrency, formatDate, formatDateTime } from 'utils/formatters';
import { useSnackbar } from 'hooks/useSnackbar';

const OpexInvoiceDetails = () => {
  const navigate = useNavigate();
  const { showSuccess, showError, snackbar, handleClose } = useSnackbar();
  const { id } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rejectDialog, setRejectDialog] = useState({ open: false, comment: '' });
  const [approveDialog, setApproveDialog] = useState({ open: false, comment: '' });

  useEffect(() => {
    fetchInvoice();
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchInvoice = async () => {
    try {
      setLoading(true);
      const response = await opexInvoiceService.getInvoice(id);
      setInvoice(response.invoice);
    } catch (error) {
      console.error('Failed to fetch invoice:', error);
      setError('Failed to load invoice details');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await opexInvoiceService.getCategories();
      // Backend returns {data: categories}, so extract the data
      const categoriesData = response.data || response;
      setCategories(categoriesData);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const handleApprove = async () => {
    try {
      await opexInvoiceService.approveInvoice(id, approveDialog.comment);
      setApproveDialog({ open: false, comment: '' });
      showSuccess('Invoice approved successfully');
      fetchInvoice();
    } catch (error) {
      console.error('Failed to approve invoice:', error);
      showError('Failed to approve invoice.');
    }
  };

  const handleReject = async () => {
    try {
      await opexInvoiceService.rejectInvoice(id, rejectDialog.comment);
      setRejectDialog({ open: false, comment: '' });
      showSuccess('Invoice rejected.');
      fetchInvoice();
    } catch (error) {
      console.error('Failed to reject invoice:', error);
      showError('Failed to reject invoice');
    }
  };

  const handleMarkAsPaid = async () => {
    try {
      await opexInvoiceService.markAsPaid(id, 'Marked as Remittance Advice from details page');
      showSuccess('Invoice marked as paid successfully.');
      fetchInvoice();
    } catch (error) {
      console.error('Failed to mark as paid:', error);
      showError('Failed to mark invoice as paid.');
    }
  };

  const handleDownloadInvoice = async () => {
    try {
      const { blob, filename } = await opexInvoiceService.downloadInvoiceFile(id);
      opexInvoiceService.downloadFile(blob, filename);
    } catch (error) {
      console.error('Failed to download file:', error);
      alert('Failed to download file');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !invoice) {
    return <Alert severity="error">{error || 'Invoice not found'}</Alert>;
  }

  return (
    <Box>
      <Stack
        direction="row"
        spacing={2}
        sx={{
          alignItems: 'center',
          mb: 3
        }}
      >
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>
          OPEX Invoice {invoice.custom_invoice_number || invoice.invoice_number}
        </Typography>
        {invoice.status === 'submitted' && (
          <>
            <Button variant="outlined" color="error" startIcon={<Cancel />} onClick={() => setRejectDialog({ open: true, comment: '' })}>
              Reject
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircle />}
              onClick={() => setApproveDialog({ open: true, comment: '' })}
            >
              Approve
            </Button>
          </>
        )}
        {invoice.status === 'approved' && (
          <Button variant="contained" color="primary" onClick={handleMarkAsPaid}>
            Mark as Paid
          </Button>
        )}
      </Stack>

      <Grid container spacing={3}>
        {/* Invoice Information */}
        <Grid size={{ xs: 12, md: 8 }}>
          <MainCard>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Invoice Information
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" color="textSecondary">
                  Invoice Number
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500
                  }}
                >
                  {invoice.custom_invoice_number || invoice.invoice_number || '-'}
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" color="textSecondary">
                  Status
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <InvoiceStatusChip status={invoice.status} />
                </Box>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" color="textSecondary">
                  Category
                </Typography>
                <Typography variant="body1">{categories[invoice.opex_category] || invoice.opex_category}</Typography>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" color="textSecondary">
                  Amount
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500
                  }}
                >
                  {formatCurrency(invoice.amount, invoice.currency)}
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" color="textSecondary">
                  Invoice Date
                </Typography>
                <Typography variant="body1">{formatDate(invoice.invoice_date)}</Typography>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" color="textSecondary">
                  Due Date
                </Typography>
                <Typography variant="body1">{invoice.due_date ? formatDate(invoice.due_date) : '-'}</Typography>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" color="textSecondary">
                  Billing Month
                </Typography>
                <Typography variant="body1">
                  {invoice.billing_month
                    ? new Date(invoice.billing_month + '-01').toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
                    : '-'}
                </Typography>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Typography variant="caption" color="textSecondary">
                  Description
                </Typography>
                <Typography variant="body1">{invoice.description || 'No description provided'}</Typography>
              </Grid>
            </Grid>
          </MainCard>
        </Grid>

        {/* Vendor & Files */}
        <Grid size={{ xs: 12, md: 4 }}>
          <MainCard>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Vendor Information
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="textSecondary">
                  Company Name
                </Typography>
                <Typography variant="body1">{invoice.vendor?.company_name || '-'}</Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="textSecondary">
                  Contact
                </Typography>
                <Typography variant="body2">{invoice.vendor?.user?.email || '-'}</Typography>
              </Box>
            </Stack>
          </MainCard>

          {invoice.invoice_file && (
            <MainCard sx={{ mt: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Attachments
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Button fullWidth variant="outlined" startIcon={<Download />} onClick={handleDownloadInvoice}>
                Download Invoice
              </Button>
            </MainCard>
          )}
        </Grid>

        {/* Timeline */}
        <Grid size={{ xs: 12 }}>
          <MainCard>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Timeline
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Stack spacing={2}>
              {invoice.submitted_at && (
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Submitted: {formatDateTime(invoice.submitted_at)}
                  </Typography>
                </Box>
              )}
              {invoice.approved_at && (
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'success.main'
                    }}
                  >
                    Approved: {formatDateTime(invoice.approved_at)}
                  </Typography>
                </Box>
              )}
              {invoice.rejected_at && (
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'error.main'
                    }}
                  >
                    Rejected: {formatDateTime(invoice.rejected_at)}
                  </Typography>
                </Box>
              )}
              {invoice.paid_at && (
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'primary.main'
                    }}
                  >
                    Paid: {formatDateTime(invoice.paid_at)}
                  </Typography>
                </Box>
              )}
            </Stack>
          </MainCard>
        </Grid>
      </Grid>

      {/* Approve Dialog */}
      <Dialog open={approveDialog.open} onClose={() => setApproveDialog({ open: false, comment: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>Approve Invoice</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Comment (optional)"
            value={approveDialog.comment}
            onChange={(e) => setApproveDialog({ ...approveDialog, comment: e.target.value })}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialog({ open: false, comment: '' })}>Cancel</Button>
          <Button onClick={handleApprove} variant="contained" color="success">
            Approve Invoice
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog.open} onClose={() => setRejectDialog({ open: false, comment: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Invoice</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Please provide a reason for rejecting this invoice.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            required
            label="Rejection Reason"
            value={rejectDialog.comment}
            onChange={(e) => setRejectDialog({ ...rejectDialog, comment: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog({ open: false, comment: '' })}>Cancel</Button>
          <Button onClick={handleReject} variant="contained" color="error" disabled={!rejectDialog.comment.trim()}>
            Reject Invoice
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleClose} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OpexInvoiceDetails;
